import { useCallback, useEffect, useRef } from 'react';
import type { MessageDetail } from '@/api';
import { usePreferences } from '@/app/preferences';

/**
 * Messages the reader marked unread while they were open (#343).
 *
 * The pane's own button cancels through `cancel()`, but Shift+U and the row
 * menu are the mail view's verbs and never see this hook. Without this, a
 * countdown that had not fired yet would mark the message read again a few
 * seconds after the reader said "unread" -- and on a message that was already
 * read, the flag flip itself restarted the countdown, sending remove-seen and
 * then add-seen back to back. Only the open message is ever noted, and the
 * note is dropped when that message closes.
 */
const keptUnread = new Set<string>();

/** The reader marked the open message unread: leave it that way while it stays open. */
export function keepUnread(messageId: string): void {
  keptUnread.add(messageId);
}

/** The reader marked this message unread while it was open (the conversation pane asks). */
export function isKeptUnread(messageId: string): boolean {
  return keptUnread.has(messageId);
}

/** The message closed: forget the reader's "unread" (the conversation pane's cleanup). */
export function releaseKeptUnread(messageId: string): void {
  keptUnread.delete(messageId);
}

/**
 * Settings → General → "Mark as read after viewing for N seconds".
 *
 * The preference is the person's (it is stored server-side and follows them
 * to every device), so it is read here through `usePreferences()` and never
 * cached per window. The rules:
 *
 * - **Off** — nothing is marked; the message stays unread until the reader
 *   says otherwise.
 * - **0 seconds** — marked the moment it is on screen, which is what Mail did
 *   before the setting existed.
 * - **N seconds** — marked if the same message is still open after N seconds.
 *   Opening another message, going back to the list or closing the window
 *   before then cancels it: a message glanced at on the way past is not read.
 *
 * Each message is marked at most once per time it is opened, and `cancel()`
 * (the reader pressing Mark unread) stops a pending mark from undoing their
 * choice a few seconds later.
 */
export function useMarkReadAfter(
  message: MessageDetail | undefined,
  onMarkRead: ((message: MessageDetail) => void) | undefined,
): { cancel: () => void } {
  const { preferences } = usePreferences();
  const { markReadEnabled, markReadDelaySeconds } = preferences;

  /** The id already marked, or deliberately left alone, for this opening. */
  const settledFor = useRef<string | undefined>(undefined);
  // Read through a ref, so a caller passing an inline arrow does not restart
  // the countdown on every render.
  const onMarkReadRef = useRef(onMarkRead);
  useEffect(() => {
    onMarkReadRef.current = onMarkRead;
  });

  const id = message?.id;
  const seen = message?.flags.includes('seen') ?? true;
  // The latest detail object, for the callback; the effect keys on the id.
  const messageRef = useRef(message);
  useEffect(() => {
    messageRef.current = message;
  });

  useEffect(() => {
    if (id === undefined) return;
    // A new opening starts from nothing, whatever happened to the last one.
    return () => {
      if (settledFor.current === id) settledFor.current = undefined;
      keptUnread.delete(id);
    };
  }, [id]);

  useEffect(() => {
    if (id === undefined || !markReadEnabled) return;
    // Opened already read: there is nothing to mark, and a later "unread" is
    // the reader's own choice, not a message waiting to be read.
    if (seen) {
      settledFor.current = id;
      return;
    }
    if (settledFor.current === id || keptUnread.has(id) || onMarkReadRef.current === undefined) return;
    const mark = () => {
      const current = messageRef.current;
      if (!current || current.id !== id || settledFor.current === id || keptUnread.has(id)) return;
      settledFor.current = id;
      onMarkReadRef.current?.(current);
    };
    const delay = Math.min(60, Math.max(0, markReadDelaySeconds));
    if (delay === 0) {
      mark();
      return;
    }
    const timer = window.setTimeout(mark, delay * 1000);
    return () => window.clearTimeout(timer);
  }, [id, seen, markReadEnabled, markReadDelaySeconds]);

  const cancel = useCallback(() => {
    if (id !== undefined) settledFor.current = id;
  }, [id]);

  return { cancel };
}
